/**
 * DemoBookCallBanner — soft, dismissable "prenota una demo" nudge for the
 * public-demo audience. Rendered ABOVE the shell row (as a flow element,
 * NOT ``position: fixed``) so it reserves its own height like the sibling
 * ``DemoBetaBanner``. No-op unless it's a demo session AND
 * ``useDemoBookCallTrigger`` says one of the "aha" conditions has fired.
 *
 * Pencil aesthetic — warm paper wash on cream + a single orange hairline
 * pill for the CTA (~90% neutral / ~10% accent), matching ``DemoBetaBanner``
 * so the two stacked banners feel like one strip. ``role="status"`` for
 * assistive tech; ``X`` control persists ``dismissed: true`` in the store
 * so the visitor never sees it again from this browser.
 */
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { Sparkles, X } from "lucide-react";

import { useDemoBookCallStore } from "./demoBookCallStore";
import { useDemoBookCallTrigger } from "./useDemoBookCallTrigger";

export function DemoBookCallBanner(): React.ReactElement | null {
  const { i18n } = useLingui();
  const { shouldShow } = useDemoBookCallTrigger();
  const modalOpen = useDemoBookCallStore((s) => s.modalOpen);
  const openModal = useDemoBookCallStore((s) => s.openModal);
  const setDismissed = useDemoBookCallStore((s) => s.setDismissed);

  // While the modal is open the banner is redundant — hide it so the two
  // orange affordances don't compete for attention.
  if (!shouldShow || modalOpen) return null;

  return (
    <div
      role="status"
      aria-label={i18n._(msg`Prenota una demo con il team Ando`)}
      className="flex w-full shrink-0 flex-wrap items-center justify-center gap-x-3 gap-y-1 border-hairline border-b bg-ando-orange/5 px-4 py-2 text-center"
    >
      <span
        aria-hidden
        className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-ando-orange/45 bg-transparent text-ando-orange"
      >
        <Sparkles size={12} strokeWidth={2} />
      </span>

      <p className="text-[12.5px] text-ink-2 leading-snug">
        <Trans>
          Ti piace Ando? <strong className="font-medium text-ink-1">Provalo sui tuoi dati reali</strong>{" "}
          — prenota 15 min col team.
        </Trans>
      </p>

      <button
        type="button"
        onClick={openModal}
        className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full bg-ando-orange px-3 font-semibold text-[12px] text-white transition-colors hover:bg-ando-orange-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
      >
        <Trans>Prenota demo</Trans>
      </button>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={i18n._(msg`Chiudi`)}
        className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-ink-3 transition-colors hover:bg-ando-ink/[0.06] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
      >
        <X size={14} strokeWidth={1.75} aria-hidden />
      </button>
    </div>
  );
}

DemoBookCallBanner.displayName = "DemoBookCallBanner";
